import { useCallback, useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { ApiError } from '../api/client';
import { askApplies, loadEventRoster } from '../api/eventRoster';
import type { EventRoster, RosterPerson } from '../api/eventRoster';
import { eventsApi } from '../api/events';
import type { EventDetail } from '../api/events';
import { requirementsApi } from '../api/requirements';
import { transportApi } from '../api/transport';
import type { SeatLedgerLine, TransportOffer, TransportPlan } from '../api/transport';
import type { Me } from '../api/auth';
import { Button } from '../ui/Button';
import { SelectField, TextField } from '../ui/Field';
import { AppHeader } from '../ui/AppHeader';
import { DISCLAIMER, Footer, Gutter, Page } from '../ui/Chrome';
import { PeopleGrid, Td, Th } from '../ui/PeopleGrid';
import { formatInZone } from '../lib/datetime';

/** Rows drawn per section before the rest go behind a control. The dev troop runs to hundreds. */
const DRAWN = 40;

/** How the RSVP filter is spelled. `none` is silence, which is its own state and never a decline. */
type Going = 'all' | 'Going' | 'Maybe' | 'NotGoing' | 'none';

/**
 * The trip summary — TROOP-130, expanded by TROOP-133, renamed by TROOP-137. A person per row, a
 * column per thing they owe, and a printable copy to take on the trip.
 *
 * **"Roster summary" named the table; "trip summary" names what it is for.** A leader printing this
 * is not looking up the roster, they are getting ready for Saturday — and the heading is now the
 * event's own name, which is what identifies the sheet once it is one of four in somebody's hand.
 * The table itself is unchanged.
 *
 * **Its own screen, not a fifth accordion on the event.** A leader working down a meeting hall
 * wants one table they can tick across; the event docket stays what it is — one read for the closed
 * state. A table of hundreds of rows inside an expander on a 390px phone is the shape CLAUDE.md
 * keeps warning about, and the dev troop has the numbers to prove it.
 *
 * **Adults and young people are separate tables**, because they are separate questions. What an
 * adult owes is their own; what a young person owes is answered by a parent, and the contact
 * column proves it — a youth row carries their guardians' details and never an address of their
 * own (rule 10). The heading uses the unit's own word for its young people (rule 16), so a Pack
 * says "Cub Scouts" without a second screen existing.
 *
 * **Contact details are asked for, not assumed.** The route is already adult-officers-only, so the
 * toggle is not the containment; it is that a page nobody asked to show contact details on should
 * not have fetched them, and a payload is the easiest place for a disclosure to sit unnoticed.
 *
 * **Printing is a real output, not a screenshot.** The chrome, the controls and the bounding all
 * disappear, every matching row is drawn, and the disclaimer comes along — a printed roster leaves
 * the building, so the notice that this product is not affiliated with Scouting America travels
 * with it.
 */
export function EventRosterScreen({
  unitId,
  eventId,
  me,
  onNavigate,
}: {
  unitId: string;
  eventId: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [roster, setRoster] = useState<EventRoster | null>(null);
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [travel, setTravel] = useState<TransportPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const [busy, setBusy] = useState(false);

  const [search, setSearch] = useState('');
  const [going, setGoing] = useState<Going>('all');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [contacts, setContacts] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const [table, detail, plan] = await Promise.all([
        loadEventRoster(unitId, eventId, contacts),
        eventsApi.detail(unitId, eventId),
        // Swallowed, not awaited into the failure path: who is driving is an addition to this
        // page, and a transport route having a bad day must not blank the roster somebody is
        // standing in a meeting hall holding.
        transportApi.plan(unitId, eventId).catch(() => null),
      ]);

      setRoster(table);
      setEvent(detail);
      setTravel(plan);
      setError(null);
    } catch (e) {
      // A youth officer lands here, and so does a member. The rule is the server's; the screen
      // renders its refusal rather than restating the two axes in the client.
      if (e instanceof ApiError && e.status === 403) {
        setRefused(true);
      } else {
        setError(e instanceof Error ? e.message : 'Could not load the summary.');
      }
    }
  }, [unitId, eventId, contacts]);

  useEffect(() => {
    void load();
  }, [load]);

  // Ctrl+P has to produce the same paper the button does, so the bound is lifted on the event
  // rather than inside the click handler. Without this a leader who printed from the browser menu
  // would get the first forty of three hundred people and no sign that the rest existed.
  useEffect(() => {
    const unbound = () => setShowAll(true);
    window.addEventListener('beforeprint', unbound);
    return () => window.removeEventListener('beforeprint', unbound);
  }, []);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const shell = (body: React.ReactNode) => (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>{body}</div>
        </Gutter>
      </section>
      <Footer />
    </Page>
  );

  if (refused) {
    return shell(
      // A youth officer lands here, and the sentence names the rule rather than implying they are
      // missing a permission somebody could grant them.
      <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Who has returned what is visible to the unit&rsquo;s adult leaders, and to each
        family&rsquo;s own parents.
      </p>,
    );
  }

  if (!roster) {
    return shell(
      <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {error ?? 'Loading the summary...'}
      </p>,
    );
  }

  const needle = search.trim().toLowerCase();

  const matching = roster.people.filter((person) => {
    if (needle && !person.displayName.toLowerCase().includes(needle)) return false;

    // Silence is its own state everywhere in this product, so it is its own option here rather
    // than being folded into "not going".
    if (going === 'none' && person.rsvpResponse) return false;
    if (going !== 'all' && going !== 'none' && person.rsvpResponse !== going) return false;

    if (outstandingOnly) {
      // Outstanding means: asked, and nothing recorded. An ask that does not reach this person is
      // not outstanding for them -- the same reason their cell is greyed rather than blank.
      const owesAnAsk = roster.columns.some(
        (column) =>
          askApplies(column, person.classification) && !person.cells[column.requirementId],
      );

      return owesAnAsk || !person.rsvpResponse;
    }

    return true;
  });

  const adults = matching.filter((p) => p.classification === 'Adult');
  const youth = matching.filter((p) => p.classification !== 'Adult');

  return shell(
    <div className="rs-root" style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PrintStyles />

      {/*
        The trip names the sheet, not the feature. "Roster summary" was the largest text on the page
        and the event's name was a caption under it, which is the wrong way round the moment this
        leaves the screen: paper gets carried to a car park with three other sheets and what
        identifies it is where everyone is going. The dates sit underneath, in the event's own zone.
      */}
      <div className="rs-head">
        <h1 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {event ? `${event.title} summary` : 'Trip summary'}
        </h1>
        {event && (
          <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            {formatInZone(event.startsAt, event.timeZoneId)}
            {' – '}
            {formatInZone(event.endsAt, event.timeZoneId)}
            {event.locationName && <> &middot; {event.locationName}</>}
          </p>
        )}
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {matching.length} of {roster.people.length} people
          {going !== 'all' && ` · ${describeGoing(going)}`}
        </p>
      </div>

      {error && (
        <p className="t-caption rs-screen-only" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {error}
        </p>
      )}

      <div className="rs-screen-only flex flex-wrap items-end" style={{ gap: 'var(--space-3)' }}>
        <div style={{ flex: '1 1 12rem' }}>
          <TextField
            label="Find a name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Start typing"
          />
        </div>

        <div style={{ flex: '0 1 12rem' }}>
          <SelectField
            label="Who is going"
            value={going}
            onChange={(e) => setGoing(e.target.value as Going)}
          >
            <option value="all">Everyone</option>
            <option value="Going">Going</option>
            <option value="Maybe">Maybe</option>
            <option value="NotGoing">Not going</option>
            {/* Its own option, never folded into "not going" -- that is how a seat count comes
                out short. */}
            <option value="none">Not said</option>
          </SelectField>
        </div>

        <Button
          variant={outstandingOnly ? 'primary' : 'secondary'}
          onClick={() => setOutstandingOnly((was) => !was)}
        >
          {outstandingOnly ? 'Showing only outstanding' : 'Only outstanding'}
        </Button>

        <Button
          variant={contacts ? 'primary' : 'secondary'}
          onClick={() => setContacts((was) => !was)}
        >
          {contacts ? 'Hiding contact details' : 'Show contact details'}
        </Button>

        <Button
          variant="secondary"
          onClick={() => {
            // Everything, not the first forty. The state change has to land before the print
            // dialog reads the page, so the call waits a frame.
            setShowAll(true);
            requestAnimationFrame(() => window.print());
          }}
        >
          <Printer size={16} aria-hidden />
          Print
        </Button>
      </div>

      <Section
        title="Adults"
        people={adults}
        roster={roster}
        unitId={unitId}
        eventId={eventId}
        contacts={contacts}
        busy={busy}
        showAll={showAll}
        onShowAll={() => setShowAll(true)}
        onRun={run}
      />

      {/* Rule 16: the unit's own word, so a Pack heads this "Cub Scouts" without a second screen
          existing anywhere. */}
      <Section
        title={roster.youthLabelPlural}
        people={youth}
        roster={roster}
        unitId={unitId}
        eventId={eventId}
        contacts={contacts}
        busy={busy}
        showAll={showAll}
        onShowAll={() => setShowAll(true)}
        onRun={run}
      />

      {roster.columns.length === 0 && (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Nothing is being asked of families for this event, so the table shows who is coming and
          nothing else.
        </p>
      )}

      <DriversSection plan={travel} zone={event?.timeZoneId ?? ''} />

      {/* A printed roster leaves the building, so the notice travels with it. On screen the page
          footer already carries it. */}
      <p className="rs-print-only t-caption" style={{ marginTop: 'var(--space-4)' }}>
        {DISCLAIMER}
      </p>
    </div>,
  );
}

function describeGoing(going: Going): string {
  if (going === 'none') return 'not said';
  if (going === 'NotGoing') return 'not going';
  return going.toLowerCase();
}

/**
 * One table: adults, or the unit's young people.
 *
 * Absent entirely when nobody matches, rather than an empty table with a heading — a leader
 * filtering to "going" on an event no adult has answered should see the young people's table, not
 * a heading over nothing.
 */
function Section({
  title,
  people,
  roster,
  unitId,
  eventId,
  contacts,
  busy,
  showAll,
  onShowAll,
  onRun,
}: {
  title: string;
  people: RosterPerson[];
  roster: EventRoster;
  unitId: string;
  eventId: string;
  contacts: boolean;
  busy: boolean;
  showAll: boolean;
  onShowAll: () => void;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
}) {
  if (people.length === 0) return null;

  const drawn = showAll ? people : people.slice(0, DRAWN);

  return (
    <section>
      <h2
        className="t-body-strong"
        style={{ color: 'var(--color-text-primary)', margin: '0 0 var(--space-2)' }}
      >
        {title} <span style={{ color: 'var(--color-text-secondary)' }}>({people.length})</span>
      </h2>

      {/* The same component the event page's Going and Forms panels render -- see PeopleGrid.
          `paper` is what turns the print-only twins on, and this is the only screen that asks. */}
      <PeopleGrid
        people={drawn}
        columns={roster.columns}
        going
        contacts={contacts}
        paper
        busy={busy}
        onGoing={(person, response) =>
          void onRun(() => eventsApi.respond(unitId, eventId, person.personId, response))
        }
        onComplete={(column, person, tier) =>
          void onRun(() =>
            requirementsApi.complete(unitId, eventId, column.requirementId, {
              subjectPersonId: person.personId,
              ...(tier ? { evidenceTier: tier } : {}),
            }),
          )
        }
        onWithdraw={(column, _person, cell) =>
          void onRun(() =>
            requirementsApi.withdraw(unitId, eventId, column.requirementId, cell.completionId),
          )
        }
        onReturnForm={(column, person, file) =>
          void onRun(() =>
            requirementsApi.returnForm(unitId, eventId, column.requirementId, file, [
              person.personId,
            ]),
          )
        }
      />

      {people.length > drawn.length && (
        <div className="rs-screen-only" style={{ marginTop: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={onShowAll}>
            Show {people.length - drawn.length} more
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * Who is driving, one block per leg.
 *
 * **On the sheet because the sheet is what gets carried.** The roster answers who is coming and
 * what they still owe; standing in a car park on the Friday the next question is which cars there
 * are and whether everybody fits, and that has been a separate screen on a phone with no signal.
 *
 * **Every number here belongs to a leg and none of them are added up.** That is the same rule the
 * docket row had to be taught (`travelValue`): seats offered on the way there say nothing about
 * seats home, and a total that nets one against the other reports a trip as covered while nobody is
 * driving anybody back. So each leg carries its own line, and there is deliberately no footer.
 *
 * The addresses are free text (`EventTransportLeg.OriginText` -- text, and it stays text), so they
 * render as given and are omitted rather than guessed at.
 *
 * Nothing renders for an event with no legs: a Monday meeting is not driven to, and three empty
 * headings on one train a reader to skip the section on the trip where it matters.
 */
function DriversSection({ plan, zone }: { plan: TransportPlan | null; zone: string }) {
  if (!plan || plan.legs.length === 0) return null;

  return (
    <section className="rs-drivers">
      <h2
        className="t-body-strong"
        style={{ color: 'var(--color-text-primary)', margin: '0 0 var(--space-2)' }}
      >
        Drivers
      </h2>

      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        {plan.legs.map((leg) => (
          <LegBlock
            key={leg.id}
            leg={leg}
            line={plan.ledger.find((l) => l.legId === leg.id)}
            offers={plan.offers.filter((o) => o.legId === leg.id)}
            zone={zone}
          />
        ))}
      </div>
    </section>
  );
}

function LegBlock({
  leg,
  line,
  offers,
  zone,
}: {
  leg: TransportPlan['legs'][number];
  line: SeatLedgerLine | undefined;
  offers: TransportOffer[];
  zone: string;
}) {
  const places = [leg.originText, leg.destinationText].filter(Boolean).join(' \u2192 ');

  return (
    <div className="rs-leg">
      <h3 className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
        {leg.label}
        {leg.departsAt && (
          <span className="t-caption" style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
            {' \u00b7 '}
            {formatInZone(leg.departsAt, zone)}
            {leg.returnsAt && ` \u2013 ${formatInZone(leg.returnsAt, zone)}`}
          </span>
        )}
      </h3>

      {places && (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {places}
        </p>
      )}

      {/* This leg's arithmetic and nobody else's. See the section remarks. */}
      {line && (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {line.riders} travelling, {line.seatsOffered} seat
          {line.seatsOffered === 1 ? '' : 's'}
          {' \u2014 '}
          {line.isCovered ? 'covered' : `short by ${-line.surplus}`}
        </p>
      )}

      {offers.length === 0 ? (
        <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 'var(--space-2) 0 0' }}>
          Nobody has offered to drive this one yet.
        </p>
      ) : (
        <div className="rs-scroll" style={{ overflowX: 'auto', marginTop: 'var(--space-2)' }}>
          <table
            className="t-body rs-table"
            style={{
              borderCollapse: 'collapse',
              minWidth: '100%',
              color: 'var(--color-text-primary)',
            }}
          >
            <thead>
              <tr>
                <Th>Driver</Th>
                <Th>Vehicle</Th>
                <Th>Plate</Th>
                <Th>Holds</Th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => (
                <tr key={offer.id} style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
                  <Td>
                    <span className="t-body-strong">{offer.driverName}</span>
                  </Td>
                  <Td>
                    {offer.vehicleDescription}
                    {offer.note && (
                      <span
                        className="t-caption"
                        style={{ color: 'var(--color-text-secondary)', display: 'block' }}
                      >
                        {offer.note}
                      </span>
                    )}
                  </Td>
                  <Td>
                    {offer.licensePlate ?? (
                      <span style={{ color: 'var(--color-text-muted)' }}>&mdash;</span>
                    )}
                  </Td>
                  {/* People the car takes, the driver included (TROOP-136) -- the same number
                      the offer was made in, so two cars offering five each is ten people in two
                      cars, and the header says "Holds" rather than "Seats" so nobody standing in a
                      car park reads it as spare room. */}
                  <Td>{offer.seatsOffered}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Print rules — a SPEC-GAP.
 *
 * `handoff/design-system.md` describes no print output at all, so these are built from nothing but
 * the tokens and are declared here rather than in `app.css`: this is the only screen in the product
 * anybody prints, and a global print stylesheet would be a promise about every other page that
 * nothing keeps.
 *
 * **The isolation is `visibility`, not `display`.** Hiding the chrome by tag name would mean
 * knowing what `AppHeader` and `Footer` render, and would break the day either changes; hiding
 * everything and revealing this subtree cannot. `visibility` also leaves the layout intact, which
 * is why the table still measures correctly on paper.
 *
 * Colours are forced to black on white. The tokens are a screen palette — `--color-status-compliant`
 * is a green that prints as pale grey on a mono printer, which is the one thing a roster must not
 * do, because the whole point is telling a filled cell from an empty one at a glance.
 */
function PrintStyles() {
  return (
    <style>{`
      /* The base hide, and the reason PeopleGrid takes a paper prop at all: this rule only
         exists on this screen, so a print twin rendered anywhere else would simply show.
         Backticks are a syntax error inside this template literal, not a style choice. */
      .rs-print-only, .pg-print-only { display: none; }

      @media print {
        /* Reveal only the report. See the component remarks for why this is visibility. */
        body * { visibility: hidden; }
        .rs-root, .rs-root * { visibility: visible; }
        .rs-root {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }

        .rs-screen-only, .pg-screen-only { display: none !important; }
        .rs-print-only, .pg-print-only { display: inline; }

        /* Ink, not tokens. A green tick prints as pale grey and stops being a tick. */
        .rs-root, .rs-root * {
          color: #000 !important;
          background: transparent !important;
          box-shadow: none !important;
        }

        /* The container scrolls on screen; on paper it must let the table wrap onto the page. */
        .rs-scroll, .pg-scroll { overflow: visible !important; }

        .rs-table, .pg-table { width: 100%; font-size: 10pt; }
        .rs-table th, .rs-table td, .pg-table th, .pg-table td {
          border: 1px solid #999 !important;
          padding: 4px 6px !important;
          vertical-align: top;
          white-space: normal !important;
        }
        .rs-table th, .pg-table th { text-align: left; font-weight: 600; }

        /* A roster runs over pages, so the heading row repeats and a person is not split in two. */
        .rs-table thead, .pg-table thead { display: table-header-group; }
        .rs-table tr, .pg-table tr { break-inside: avoid; }
        section { break-inside: auto; }

        /*
          Sizes in points, because the type tokens are a screen scale and resolve to whatever the
          viewport was when the dialog opened. The trip's name is the largest thing on the sheet and
          everything else steps down from it -- that ordering is the whole reason the heading
          changed, so it has to survive the reset above.
        */
        .rs-head h1 { font-size: 16pt; line-height: 1.2; }
        .rs-head p { font-size: 9pt; }
        .rs-root h2 { font-size: 11pt; }
        .rs-root h3 { font-size: 10pt; }

        /* The cars start a page of their own, and one car is never split across two. */
        .rs-drivers { break-before: page; }
        .rs-leg { break-inside: avoid; }
      }
    `}</style>
  );
}
